import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  type AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  Injector,
  inject,
  type OnInit,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import type {
  Circle,
  CircleMarker,
  LatLngBounds,
  Map as LeafletMap,
  Marker,
  MarkerCluster,
  MarkerClusterGroup,
} from 'leaflet';
import './leaflet-markercluster.types';
import { LocateFixed, LucideAngularModule, PartyPopper } from 'lucide-angular';
import type { EventType } from '../../../server/models/event-item.model';
import type { LocationItem } from '../../core/models/location-item.model';
import type { EventItem } from '../../core/models/middleware/event-item.model';
import { EventService } from '../../core/services/event.service';
import { LocationService } from '../../core/services/location.service';

@Component({
  selector: 'app-locations',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './locations.component.html',
})
export class LocationsComponent implements AfterViewInit, OnInit {
  public mapContainer = viewChild('mapContainerElement', { read: ElementRef<HTMLDivElement> });
  public mapElement = viewChild('mapElement', { read: ElementRef<HTMLDivElement> });
  public hideMap = signal<boolean>(true);
  protected readonly LocateFixed = LocateFixed;
  protected readonly PartyPopper = PartyPopper;
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly locationService = inject(LocationService);
  private readonly eventService = inject(EventService);
  private readonly platformId = inject(PLATFORM_ID);
  private locations = signal<LocationItem[]>([]);
  private map?: LeafletMap;
  private userMarker?: CircleMarker;
  private userAccuracyCircle?: Circle;
  private locationMarkers: Marker[] = [];
  private markerClusterGroup?: MarkerClusterGroup;
  private leaflet?: typeof import('leaflet');

  constructor() {
    effect(() => {
      if (this.eventService.getCurrentEvent()) {
        this.locationService
          .getLocationsForEvent((this.eventService.getCurrentEvent() as EventItem<EventType>).documentId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (locations) => {
              this.locations.set(locations.filter((location) => !!location.coordinates));
            },
            error: (error: Error) => {
              console.error('Error fetching locations:', error);
              this.locations.set([]);
            },
          });
      } else {
        this.locations.set([]);
      }
    });
  }

  ngOnInit(): void {
    const eventDocumentId = this.activatedRoute.snapshot.params.eventDocumentId as string;
    const eventType = this.activatedRoute.snapshot.params.eventType as EventType;
    if (this.eventService.getCurrentEvent()?.documentId !== eventDocumentId) {
      this.eventService.getEvent(eventDocumentId, eventType).subscribe((value) => {
        this.eventService.setCurrentEvent(value);
      });
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const leafletModule = await import('leaflet');
        this.leaflet = leafletModule.default || leafletModule;
        await import('leaflet.markercluster');

        this.map = this.leaflet.map('map', {
          zoomControl: false,
        });

        this.leaflet
          .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
          })
          .addTo(this.map);

        const watchId = navigator.geolocation.watchPosition(
          (position) => this.updateUserMarker(position),
          (error) => console.error('Error retrieving geolocation:', error)
        );

        effect(
          () => {
            const locs = this.locations();
            this.updateEventMarkers(locs);
          },
          { injector: this.injector }
        );

        this.destroyRef.onDestroy(() => {
          navigator.geolocation.clearWatch(watchId);
          if (this.markerClusterGroup && this.map) {
            this.map.removeLayer(this.markerClusterGroup);
            this.markerClusterGroup = undefined;
          }
          if (this.map) {
            this.map.remove();
            this.map = undefined;
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }
  }

  isUserPositionKnown(): boolean {
    return !!this.userAccuracyCircle;
  }

  goToUserPosition(): void {
    if (!this.map || !this.userAccuracyCircle) return;

    this.goToPosition(this.userAccuracyCircle.getBounds());
  }

  goToEventPosition(): void {
    if (!this.leaflet || !this.markerClusterGroup || this.locationMarkers.length === 0) return;

    this.goToPosition(this.markerClusterGroup.getBounds());
  }

  private updateUserMarker(position: GeolocationPosition) {
    if (!this.map || !this.leaflet) return;

    this.userMarker?.remove();
    this.userAccuracyCircle?.remove();

    if (!position) return;

    this.userAccuracyCircle = this.leaflet
      .circle([position.coords.latitude, position.coords.longitude], {
        radius: position.coords.accuracy,
        stroke: false,
        fillOpacity: 0.2,
      })
      .addTo(this.map);
    this.userMarker = this.leaflet
      .circleMarker([position.coords.latitude, position.coords.longitude], {
        radius: 10,
        color: '#FFFFFF',
        weight: 2,
        fillColor: '#3388FF',
        fillOpacity: 1.0,
      })
      .addTo(this.map);
  }

  private updateEventMarkers(locations: LocationItem[]) {
    if (!this.map || !this.leaflet) return;

    if (this.markerClusterGroup) {
      this.map.removeLayer(this.markerClusterGroup);
    }
    this.locationMarkers = [];

    if (locations.length === 0) {
      this.hideMap.set(true);
      return;
    }
    this.hideMap.set(false);
    this.map.fitWorld();

    this.markerClusterGroup = this.leaflet.markerClusterGroup({
      iconCreateFunction: (cluster: MarkerCluster) => {
        const count = cluster.getChildCount();
        return this.leaflet!.divIcon({
          html: `<div class="cluster-icon"><span>${count}</span></div>`,
          className: 'custom-cluster-icon',
          iconSize: [40, 40],
        });
      },
      maxClusterRadius: 80,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });

    const highlightLocationId = this.activatedRoute.snapshot.queryParams.highlight as string;
    let highlightFound = false;
    let highlightMarker: Marker | undefined;

    locations
      .filter((loc) => loc.coordinates !== undefined)
      .forEach((loc) => {
        if (!loc.coordinates?.latitude || !loc.coordinates?.longitude) return;
        const isHighlighted = highlightLocationId && highlightLocationId === loc.documentId;
        const highlightClass = isHighlighted ? 'highlighted' : '';

        const labelText = loc.name;
        const estimatedWidth = Math.max(100, Math.min(280, labelText.length * 9 + 30));

        const genericIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>`;

        const marker = this.leaflet?.marker([loc.coordinates.latitude, loc.coordinates.longitude], {
          icon: this.leaflet.divIcon({
            html: `<div class="glassmorphic-marker ${highlightClass}">
              <div class="glassmorphic-marker-icon">${genericIcon}</div>
              <div class="glassmorphic-marker-label">${labelText}</div>
            </div>`,
            className: 'glassmorphic-marker-container',
            iconSize: [estimatedWidth, 44],
            iconAnchor: [estimatedWidth / 2, 50],
          }),
        });

        const hasRichContent = loc.imageUrl || (loc.description && loc.description.trim().length > 0);
        if (hasRichContent) {
          marker?.bindPopup(
            `${loc.imageUrl ? `<div class="popup-image"><img src="${loc.imageUrl}" alt="${loc.name}" /></div>` : ''}
             ${loc.description ? `<div class="popup-description">${loc.description}</div>` : ''}`,
            {
              closeButton: false,
              className: 'mobile-optimized-popup',
              maxWidth: window.innerWidth < 640 ? window.innerWidth - 40 : 320,
              minWidth: window.innerWidth < 640 ? window.innerWidth - 40 : 280,
              offset: [0, -12],
            }
          );
        }

        if (marker) {
          this.markerClusterGroup?.addLayer(marker);
          this.locationMarkers.push(marker);

          if (highlightLocationId && highlightLocationId === loc.documentId) {
            highlightFound = true;
            highlightMarker = marker;
          }
        }
      });

    if (this.markerClusterGroup) {
      this.map.addLayer(this.markerClusterGroup);
    }

    if (highlightFound && highlightMarker) {
      highlightMarker.openPopup();
      this.goToPosition(this.leaflet!.latLngBounds([highlightMarker.getLatLng()]));
    } else if (!highlightFound) {
      this.goToEventPosition();
    }
  }

  private goToPosition(bounds: LatLngBounds): void {
    this.map?.fitBounds(bounds, {
      paddingTopLeft: [50, 150],
      paddingBottomRight: [100, 50],
      maxZoom: 17,
    });
  }
}
